import { useCallback, useRef, useState } from "react";

import {
  SelectSheet,
  SelectSheetOption,
} from "../../../../components/MobileForm";
import { useResponsive } from "../../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../../utils/constants";
import DropdownMenu from "./DropdownMenu";
import FilterDropdown from "./FilterDropdown";
import {
  Chevron,
  DropdownWrapper,
  MenuItem,
  PeriodControls,
  Seg,
  SegGroup,
} from "./styles";
import { ALL_VALUE } from "./constants";
import { MonthOption, PeriodSelectorProps } from "./types";
import { useDropdownDismiss } from "../../../../hooks/useDropdownDismiss";

const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  monthOptions,
  activeMonth,
  rangeValue,
  rangeOptions,
  yearOptions,
  yearValue,
  monthChoiceOptions,
  monthChoiceValue,
  showYear,
  showMonthChoice,
  onSelectMonth,
  onSelectRange,
  onSelectYear,
  onSelectMonthChoice,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);
  const [isOpen, setIsOpen] = useState(false);
  const customRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  useDropdownDismiss(customRef, menuRef, isOpen && !isCompact, close);

  const handleMonth = useCallback(
    (month: MonthOption) => {
      onSelectMonth(month);
    },
    [onSelectMonth],
  );

  const handleRange = useCallback(
    (option: SelectSheetOption) => {
      onSelectRange(option);
      setIsOpen(false);
    },
    [onSelectRange],
  );

  const isCustomActive = rangeValue !== null;
  const isMonthChoiceActive = monthChoiceValue.value !== ALL_VALUE;

  return (
    <PeriodControls>
      <SegGroup role="group" aria-label="Select period">
        {monthOptions.map((month) => {
          const isSelected =
            activeMonth !== null &&
            activeMonth.year === month.year &&
            activeMonth.month === month.month;
          return (
            <Seg
              key={`${month.year}-${month.month}`}
              type="button"
              $selected={isSelected}
              aria-pressed={isSelected}
              onClick={() => handleMonth(month)}
            >
              {month.label}
            </Seg>
          );
        })}

        <DropdownWrapper ref={customRef}>
          <Seg
            type="button"
            $selected={isCustomActive}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            onClick={toggle}
          >
            {isCustomActive ? rangeValue.label : "Custom"}
            <Chevron $isOpen={isOpen} />
          </Seg>

          {isCompact ? (
            <SelectSheet
              isOpen={isOpen}
              title="Select range"
              options={rangeOptions}
              selectedValue={rangeValue?.value}
              onSelect={handleRange}
              onClose={close}
            />
          ) : (
            isOpen && (
              <DropdownMenu
                anchorRef={customRef}
                menuRef={menuRef}
                align="left"
              >
                {rangeOptions.map((option) => {
                  const isSelected = rangeValue?.value === option.value;
                  return (
                    <MenuItem
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      $selected={isSelected}
                      onClick={() => handleRange(option)}
                    >
                      {option.label}
                    </MenuItem>
                  );
                })}
              </DropdownMenu>
            )
          )}
        </DropdownWrapper>
      </SegGroup>

      {showYear && (
        <FilterDropdown
          label="Year"
          value={yearValue}
          options={yearOptions}
          isActive={isCustomActive === false}
          onChange={onSelectYear}
          testId="filter-year"
        />
      )}
      {showMonthChoice && (
        <FilterDropdown
          label="Month"
          value={monthChoiceValue}
          options={monthChoiceOptions}
          isActive={isMonthChoiceActive}
          onChange={onSelectMonthChoice}
          testId="filter-month"
        />
      )}
    </PeriodControls>
  );
};

export default PeriodSelector;
